import CrossHouseRelations from './components/crossHouseRelations';
import House from './components/house';
import { ChildRoom, ParentRoom } from './components/room';
import { createInitialValues } from './utils/createInitialValues';

class HousesModel {
  constructor() {
    this._houses = new Map(); // [houseId, House]
    this._crossParents = new CrossHouseRelations(); // [houseId, Set<roomId>]
    this._crossChildren = new CrossHouseRelations(); // [houseId, Set<roomId>]
    this._values = createInitialValues();
  }

  get houses() {
    return this._houses;
  }

  get values() {
    return this._values;
  }

  addHouse(house, { shouldCalculateValues = false } = {}) {
    this._houses.set(house.id, new House(house));

    if (shouldCalculateValues) {
      this._calculateValues(this._createHousesIteratee());
    }
  }

  addHouses(houses) {
    houses.forEach((house) => this.addHouse(house));
    this._calculateValues(this._createHousesIteratee());
  }

  deleteHouse(houseId, { shouldCalculateValues = false } = {}) {
    const house = this._houses.get(houseId);

    const calculateCbs = [];

    if (shouldCalculateValues) {
      calculateCbs.push(this._createHousesIteratee());

      if (house.roomsWithRelation.size > 0)
        calculateCbs.push(this._createRelatedRoomsIteratee());

      if (house.excludedRoomIds.size > 0)
        calculateCbs.push(this._createExcludedIteratee());
    }

    const crossParentRoomIds = this._crossParents.houses.get(houseId);
    const crossChildRoomIds = this._crossChildren.houses.get(houseId);

    if (crossParentRoomIds) {
      crossParentRoomIds.forEach((room) => {
        this.deleteRelation(room.payload);
      });
    }

    if (crossChildRoomIds) {
      crossChildRoomIds.forEach((room) => {
        this.deleteRoomFromRelation(room.payload);
      });
    }

    this._houses.delete(houseId);

    if (calculateCbs.length > 0) {
      this._calculateValues(calculateCbs);
    }
  }

  deleteHouses(houseIds) {
    if (!houseIds) {
      this._houses.clear();
      this._crossParents = new CrossHouseRelations();
      this._crossChildren = new CrossHouseRelations();
      this._values = createInitialValues();
      return;
    }

    houseIds.forEach((houseId) => this.deleteHouse(houseId));

    const cbs = [
      this._createHousesValueIteratee(),
      this._createRelatedRoomsValuesIteratee(),
      this._createExcludedValueIteratee(),
    ];

    this._calculateValues(cbs);
  }

  addRelation(room, relatedRooms) {
    const parentRoom = new ParentRoom(room);

    const house = this._houses.get(room.houseId);

    house.roomsWithRelation.set(parentRoom.id, parentRoom);

    relatedRooms.forEach((r) => {
      const childRoom = new ChildRoom(r, parentRoom);

      parentRoom.children.add(childRoom);

      if (childRoom.houseId !== parentRoom.houseId) {
        const childHouse = this._houses.get(childRoom.houseId);

        childHouse.roomsWithRelation.set(childRoom.id, childRoom);

        this._crossChildren.addRoom(childRoom.houseId, childRoom);
        this._crossParents.addRoom(parentRoom.houseId, parentRoom);
      } else {
        house.roomsWithRelation.set(childRoom.id, childRoom);
      }
    });

    this._calculateValues(this._createRelatedRoomsIteratee());
  }

  deleteRelation(roomData, { shouldCalculateValues = false } = {}) {
    const house = this._houses.get(roomData.houseId);

    const parentRoom = house.roomsWithRelation.get(roomData.id);

    house.roomsWithRelation.delete(parentRoom.id);

    parentRoom.children.forEach((child) => {
      if (child.houseId !== parentRoom.houseId) {
        const childHouse = this._houses.get(child.houseId);

        childHouse.roomsWithRelation.delete(child.id);

        this._crossChildren.deleteRoom(child.houseId, child);
        this._crossParents.deleteRoom(parentRoom.houseId, parentRoom);
      } else {
        house.roomsWithRelation.delete(child.id);
      }
    });

    if (shouldCalculateValues) {
      this._calculateValues(this._createRelatedRoomsIteratee());
    }
  }

  deleteRoomFromRelation(roomData, { shouldCalculateValues = false } = {}) {
    const house = this._houses.get(roomData.houseId);

    const room = house.roomsWithRelation.get(roomData.id);

    const parentRoom = room.parent;

    if (parentRoom.children.size === 1) {
      this.deleteRelation(parentRoom.payload, { shouldCalculateValues });
      return;
    }

    house.roomsWithRelation.delete(room.id);

    this._crossChildren.deleteRoom(room.houseId, room);

    parentRoom.children.delete(room);

    if (!parentRoom.hasChildFromOtherHouses()) {
      this._crossParents.deleteRoom(parentRoom.houseId, parentRoom);
    }

    if (shouldCalculateValues) {
      this._calculateValues(this._createRelatedRoomsIteratee());
    }
  }

  setRoomCounts(dataMap) {
    dataMap.forEach((count, houseId) => {
      this._houses.get(houseId).setRoomsCount(count);
    });
  }

  excludeRoom(roomData, { shouldCalculateValues = false } = {}) {
    const house = this._houses.get(roomData.houseId);

    const cbs = [this._createExcludedIteratee()];

    house.exclude(roomData.id);

    const room = house.roomsWithRelation.get(roomData.id);

    if (room) {
      room.isParent()
        ? this.deleteRelation(room.payload)
        : this.deleteRoomFromRelation(room.payload);

      cbs.push(this._createRelatedRoomsIteratee());
    }

    if (shouldCalculateValues) {
      this._calculateValues(cbs);
    }
  }

  excludeRooms(roomsData) {
    let hasRelations = false;

    const cbs = [this._createExcludedIteratee()];

    roomsData.forEach((roomData) => {
      const house = this._houses.get(roomData.houseId);

      if (house.roomsWithRelation.has(roomData.id)) {
        hasRelations = true;
      }

      this.excludeRoom(roomData);
    });

    if (hasRelations) {
      cbs.push(this._createRelatedRoomsIteratee());
    }

    this._calculateValues(cbs);
  }

  includeRoom(roomData, { shouldCalculateValues = false } = {}) {
    const house = this._houses.get(roomData.houseId);

    house.include(roomData.id);

    if (shouldCalculateValues) {
      this._calculateValues(this._createExcludedIteratee());
    }
  }

  includeRooms(roomsData) {
    roomsData.forEach((roomData) => this.includeRoom(roomData));
    this._calculateValues(this._createExcludedIteratee());
  }

  houseCrossParentsToRender(houseId) {
    const rooms = this._crossParents.houses.get(houseId);

    if (!rooms) return null;

    const output = [];

    rooms.forEach((room) => output.push(room.toRender()));

    return output;
  }

  houseCrossChildrenToRender(houseId) {
    const rooms = this._crossChildren.houses.get(houseId);

    if (!rooms) return null;

    const output = [];
    const addedIds = new Set();

    rooms.forEach((room) => {
      const parent = room.parent;

      if (!addedIds.has(parent.id)) {
        output.push(parent.toRender());
        addedIds.add(parent.id);
      }
    });

    return output;
  }

  clone() {
    const cloned = new HousesModel();
    for (const key of Object.keys(this)) {
      cloned[key] = this[key];
    }

    return cloned;
  }

  _calculateValues(cb) {
    const cbArray = Array.isArray(cb) ? cb : [cb];

    this._houses.forEach((house) => {
      cbArray.forEach((cb) => cb(house));
    });
  }

  _createHousesIteratee() {
    this._values.houses = [];
    return (house) => {
      this._values.houses.push(house.payload);
    };
  }

  _createRelatedRoomsIteratee() {
    this._values.relatedRoomIds = new Set();
    this._values.relatedRoomsForRender = [];

    return (house) => {
      house.roomsWithRelation.forEach((room, roomId) => {
        this._values.relatedRoomIds.add(roomId);

        if (room.isParent()) {
          this._values.relatedRoomsForRender.push(room.toRender());
        }
      });
    };
  }

  _createExcludedIteratee() {
    this._values.excludedRoomIds = [];

    return (house) => {
      const excluded = house.excludedRoomIds;

      if (excluded.size > 0) {
        this._values.excludedRoomIds.push(...excluded);
      }
    };
  }
}

export default HousesModel;
